'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  ReadonlyURLSearchParams,
  useParams,
  usePathname,
  useSearchParams,
} from 'next/navigation';
import useSWR from 'swr';

import { useUserStore as useRestaurantProfileStore } from '@/stores/restuarant/user';
import { getCustomerNotifications } from '@/clients/customer/notifications';
import CountrySelector from '@/components/shared/CountrySelector';
import { useUserStore } from '@/stores/cutomer/user';
import useWindowSize from '@/hooks/useWindowSize';
import { BRAND_LOGO_ALT_TEXT, storage_keys } from '@/constant/configs';
import useStore from '@/stores/useStore';
import useAuth from '@/hooks/useAuth';

import BannerNotificationCard from '@/components/BannerNotificationCard';
import NotificationsPopover from '@/components/NotificationsPopover';
import HeaderProfileRestaurant from './HeaderProfileRestaurant';
import LocationSelector from '@/shared/LocationSelector';
import UserSignIn from '@ui/Icons/UserSignIn';
import HeaderProfile from './HeaderProfile';
import HeaderNavLinks from './HeaderNavLinks';
import DrawerMenu from './DrawerMenu';
import Menu from '@ui/Icons/Menu';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

import { restaurant, storeFront } from '@/lib/utils/routeLinks';
import { useTranslations } from 'next-intl';
import CategoriesMegaMenu from './CategoriesMegaMenu';
import HeaderOnlineShopSearch from './HeaderOnlineShopSearch';

const fetchCustomerNotifications = async () => {
  let filters: any = {};
  const params = window.location.search.substring(1);

  const urlParams = new URLSearchParams(params);
  if (urlParams.size > 0) {
    filters = Object.fromEntries(urlParams);
  }
  return getCustomerNotifications(filters).then((res) => res.data);
};

interface HeaderProps {
  userAuthToken?: string;
  resAuthToken?: string;
  appSettingsData?: any;
}

export default function Header({
  userAuthToken,
  resAuthToken,
  appSettingsData,
}: Readonly<HeaderProps>) {
  const [loading, setLoading] = useState<boolean>(true);
  const [openDrawerMenu, setOpenDrawerMenu] = useState<boolean>(false);
  const pathname = usePathname();
  const { width } = useWindowSize();
  const { isCustomerLoggedIn } = useAuth();
  const handleMenuShow = () => {
    setOpenDrawerMenu(true);
  };
  const handleMenuCloese = () => {
    setOpenDrawerMenu(false);
  };

  const params = useParams();

  const locale: string = params.locale as string;
  const [lang] = locale.split('-');
  const t = useTranslations();

  const user = useUserStore((state) => state.user);
  const getUser = useUserStore((state) => state.getUser);
  const getRestaurantUser = useRestaurantProfileStore((state) => state.getUser);
  const restaurantProfile = useStore(
    useRestaurantProfileStore,
    (state) => state.user
  );

  useEffect(() => {
    setLoading(false);
    getUser(userAuthToken);
    getRestaurantUser(resAuthToken);
  }, [getUser, getRestaurantUser, userAuthToken, resAuthToken]);

  const { data: notificationsData, isValidating } = useSWR(
    isCustomerLoggedIn ? ['/notifications/for-page'] : null,
    fetchCustomerNotifications,
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 30000,
    }
  );

  const bnfcFromAdmin = appSettingsData?.banner_notifications;

  /* ALL LOGIC FOR RENDERING TEXT AND LINKS IN HEADER */
  const vendorPages = [
    storeFront.forVendor.en(),
    storeFront.forVendor.de(),
    storeFront.forHotels.de(),
    storeFront.forHotels.en(),
    storeFront.forCompany.en(),
    storeFront.forCompany.de(),
  ];

  const renderVendorPages = [
    storeFront.forVendor.de(),
    storeFront.forVendor.en(),
    storeFront.forHotels.de(),
    storeFront.forHotels.en(),
    storeFront.forCompany.en(),
    storeFront.forCompany.de(),
    storeFront.partnerGastronomien.de(),
    storeFront.partnerGastronomien.en(),
  ];
  const isCompanyPage = [
    storeFront.forCompany.de(),
    storeFront.forCompany.en(),
  ].some((route) => pathname === `/${locale}${route}`);
  const isHomePage = pathname === `/${locale}`;
  const isVendorPage = vendorPages.some(
    (page) => pathname === `/${locale}${page}`
  );
  const loginPath = isVendorPage ? restaurant.login.de() : '/login';

  const customerLoginText = lang === 'en' ? 'Login' : 'Anmelden';
  const vendorLoginText = lang === 'en' ? 'Business login' : 'Geschäfts-Login';
  const loginButtonText = isVendorPage ? vendorLoginText : customerLoginText;
  /* ------------------ */

  //= Get Query Params values
  const searchParams: ReadonlyURLSearchParams = useSearchParams();
  const pageType: string | null = searchParams.get('type');
  const lat: string | null = searchParams.get('lat');
  const lng: string | null = searchParams.get('lng');
  const restaurantSearchPathName = `${pathname}?lat=${lat}&lng=${lng}&type=${pageType}`;

  //= Add sticky header
  useEffect(() => {
    const stickySearch: any = document.querySelector('#stickyHeader');
    const isSticky = (): void => {
      const scrollTop: number = window.scrollY;
      const scrollTopDevices = width <= 767.99 ? 77 : 87;
      if (scrollTop >= scrollTopDevices) {
        stickySearch.classList.add('sticky-header');
      } else {
        stickySearch.classList.remove('sticky-header');
      }
    };

    window.addEventListener('scroll', isSticky);
    return (): void => {
      window.removeEventListener('scroll', isSticky);
    };
  }, []);

  const renderVendorLinks = renderVendorPages.some(
    (value) => pathname === `/${locale}${value}`
  );

  return (
    <>
      <header
        id="stickyHeader"
        className="relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-full after:bg-[linear-gradient(90deg,_#9747FF_10.83%,_#EF0000_30.81%,_#8102FF_46.74%,_#00D689_61.86%,_#1C69FF_79.55%,_#0086BF_92.32%)] after:opacity-30 after:content-['']"
      >
        <div className="container flex items-center justify-between bg-transparent pb-[20px] pt-[16px]">
          {/* Logo */}
          <Link href={'/'} className="flex-shrink-0">
            <Image
              src="/logo.svg"
              alt={BRAND_LOGO_ALT_TEXT}
              width={158}
              height={41}
            />
          </Link>

          {/* Categories Button - New Addition */}
          {!isCompanyPage && (
            <div className="hidden xl:flex">
              <CategoriesMegaMenu lang={lang as 'en' | 'de'} />
            </div>
          )}

          <div className="mx-4 hidden flex-1 xl:flex">
            <HeaderOnlineShopSearch />
          </div>

          {/* Location Selector (keeping existing conditional rendering) */}
          {restaurantSearchPathName.startsWith(
            `/${locale}${storeFront.restaurants.de()}`
          ) && searchParams.has('lat') ? (
            <LocationSelector
              pathName={restaurantSearchPathName}
              lat={lat}
              lng={lng}
              pageType={pageType}
            />
          ) : null}

          {/* Navigation */}
          <nav className="ml-auto hidden items-center xl:flex">
            <CountrySelector />

            {loading && (
              <div className="mr-[35px] flex gap-x-[35px]">
                <div className="h-[20px] w-[70px] animate-pulse rounded-xl bg-slate-500" />
                <div className="h-[20px] w-[70px] animate-pulse rounded-xl bg-slate-500" />
              </div>
            )}

            {renderVendorLinks && <HeaderNavLinks />}

            {/* New "So funktioniert's" Link */}
            {isHomePage && (
              <Link
                href="#howItWorks"
                className="flex items-center gap-1 font-bold text-gray-900 hover:text-black"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById('howItWorks')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <div className="">
                  <QuestionMarkCircleIcon className="w-5" />
                </div>
                <div>{t('common.how_it_works')}</div>
              </Link>
            )}
            {/* Login Section */}
            {loading ? (
              <div className="flex h-[50px] w-[80px] items-center justify-start">
                <div className="h-[41px] w-[41px] shrink-0 animate-pulse rounded-full bg-slate-600"></div>
              </div>
            ) : (
              <>
                {user ? (
                  <>
                    <NotificationsPopover
                      notificationsData={notificationsData}
                      isValidating={isValidating}
                      crFrom="nfc_customer_popover"
                      viewLink="/account?tab=notifications"
                      nfcKey={storage_keys.last_customer_notification}
                      mutateKey={'/customer-notifications-page'}
                    />
                    <HeaderProfile />
                  </>
                ) : restaurantProfile ? (
                  <HeaderProfileRestaurant />
                ) : (
                  <Link
                    href={loginPath}
                    className="ml-[35px] flex items-center rounded-[30px] bg-[#1C2B3A] px-[25px] py-[15px] text-h7/[1.3] font-normal text-white transition-colors hover:bg-[#161F2A]"
                  >
                    {loginButtonText}
                    <UserSignIn className="ml-[11.85px]" />
                  </Link>
                )}
              </>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <div className="item-center ml-auto flex space-x-1 xl:hidden">
            {user && (
              <NotificationsPopover
                notificationsData={notificationsData}
                isValidating={isValidating}
                crFrom="nfc_customer_popover"
                viewLink="/account?tab=notifications"
                nfcKey={storage_keys.last_customer_notification}
                mutateKey={'/customer-notifications-page'}
              />
            )}
            <button
              onClick={handleMenuShow}
              className="ml-auto flex h-[41px] w-[39px] items-center justify-center rounded-[5px] border border-[#F1F1F4] xl:hidden"
              aria-label={t('aria.open_menu')}
            >
              <Menu />
            </button>
          </div>
        </div>

        {openDrawerMenu ? (
          <DrawerMenu
            handleMenuClose={handleMenuCloese}
            showVendorMenu={renderVendorLinks}
          />
        ) : null}
      </header>

      {/* Banner Notifications Section */}
      <div className="bnfc-section container">
        {bnfcFromAdmin &&
          bnfcFromAdmin?.length > 0 &&
          pathname === `/${locale}` && (
            <BannerNotificationCard
              bnfc={bnfcFromAdmin[0]}
              bnfcKey={storage_keys.storefront_homepage_bnfc}
              cardClass="mt-5"
            />
          )}
      </div>
    </>
  );
}
